package com.islamicmedia.shared.domain.model

import kotlin.random.Random

/** The level a kid picks before an Islamic Trivia round. Each round still gets harder as it goes. */
enum class TriviaDifficulty(val label: String, /** The bank level of each question, in order. */ internal val levels: List<Int>) {
    EASY("Easy", listOf(1, 1, 1, 1, 2, 2, 2, 2, 3, 3)),
    MEDIUM("Medium", listOf(2, 2, 2, 3, 3, 3, 3, 4, 4, 4)),
    HARD("Hard", listOf(3, 3, 3, 4, 4, 4, 4, 5, 5, 5))
}

/** One question as asked: the answers in the order shown, and which one is right. */
data class TriviaCard(val question: TriviaQuestion, val answers: List<String>, val correctIndex: Int)

/**
 * Islamic Trivia (KIDZONE, 8 and over): rounds of [ROUND_SIZE] questions from the vetted [IslamicTriviaBank],
 * never written by a model. Questions and answer order are shuffled every round, and the questions of the
 * last few rounds are asked again only when a level has run out of fresh ones.
 */
object IslamicTrivia {
    const val ROUND_SIZE = 10
    private const val REMEMBERED = 60

    /** IDs asked recently, oldest first. In memory: a new app start may repeat, which is fine. */
    private val recent = ArrayDeque<String>()

    fun round(difficulty: TriviaDifficulty, random: Random = Random.Default): List<TriviaCard> {
        val byLevel = IslamicTriviaBank.QUESTIONS.groupBy { it.level }
        val picked = mutableListOf<TriviaQuestion>()
        for (level in difficulty.levels) {
            val pool = byLevel[level].orEmpty().filter { q -> picked.none { it.id == q.id } }
            if (pool.isEmpty()) continue
            val fresh = pool.filter { it.id !in recent }
            picked += (fresh.ifEmpty { pool }).random(random)
        }
        picked.forEach { markAsked(it.id) }
        return picked.map { q ->
            val answers = q.answers.shuffled(random)
            TriviaCard(q, answers, answers.indexOf(q.answers.first()))
        }
    }

    private fun markAsked(id: String) {
        recent.remove(id)
        recent.addLast(id)
        while (recent.size > REMEMBERED) recent.removeFirst()
    }
}
